import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { BuddyLogo } from "../components/Brand";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export default function Pricing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api
      .get("/payments/plans")
      .then((r) => setPlans(r.data))
      .catch((err) => console.warn("Failed to load pricing plans:", err));
  }, []);

  async function checkout(lookup_key) {
    if (!user) { window.location.href = "/login"; return; }
    try {
      const r = await api.post("/payments/checkout", { lookup_key, quantity: 1, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || t("pricing.checkoutFailed")); }
  }

  return (
    <div className="min-h-screen adult-bg">
      <header className="hb-glass sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/"><BuddyLogo /></Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/"><Button variant="ghost" data-testid="pricing-back-home">{t("common.backHome")}</Button></Link>
          </div>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-1 rounded-full bg-yellow-100 text-yellow-900 px-3 py-1 text-xs font-semibold"><Sparkles className="w-3.5 h-3.5" /> {t("pricing.eyebrow")}</div>
          <h1 className="font-adult font-bold text-4xl sm:text-5xl text-slate-900 mt-3">{t("pricing.title")}</h1>
          <p className="text-slate-600 mt-2">{t("pricing.subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          <div className="hb-card p-6">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{t("pricing.free")}</div>
            <div className="mt-2 font-adult font-bold text-3xl text-slate-900">$0</div>
            <div className="text-slate-500 text-sm">{t("pricing.freeSub")}</div>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {[t("pricing.coreSubjects"), t("pricing.limitedDaily"), t("pricing.basicProgress")].map((tx)=>(
                <li key={tx} className="flex gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5" />{tx}</li>
              ))}
            </ul>
            <Link to="/activation" className="block mt-6">
              <Button variant="outline" className="w-full rounded-full" data-testid="pricing-btn-activation">{t("pricing.haveCode")}</Button>
            </Link>
          </div>
          {plans.map((p) => (
            <div key={p.lookup_key} className="hb-card p-6 border-2 border-slate-200">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{p.name}</div>
              <div className="mt-2 font-adult font-bold text-3xl text-slate-900">${(p.amount/100).toFixed(2)}</div>
              <Button className="w-full mt-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid={`pricing-btn-${p.lookup_key}`}
                      onClick={() => checkout(p.lookup_key)}>{t("common.getStarted")}</Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 text-center mt-8">{t("pricing.testCardHint")}</p>
      </main>
    </div>
  );
}
